<div class="mf-page">
  <div class="mf-save-bar">
    <div class="mf-save-bar__inner">
      <div class="mf-save-bar__left">
        <button
          type="button"
          class="mf-save-bar__back"
          [title]="'modules.form.backTitle' | translate"
          [attr.aria-label]="'modules.form.backAriaLabel' | translate"
          (click)="onCancel()"
        >
          <mat-icon aria-hidden="true">chevron_left</mat-icon>
        </button>
        <div class="mf-save-bar__heading">
          <span class="mf-save-bar__eyebrow">
            @if (isEdit) {
              {{ 'modules.form.eyebrowExisting' | translate: { id: moduleLabel() } }}
            } @else {
              {{ 'modules.form.eyebrowNew' | translate }}
            }
          </span>
          <span class="mf-save-bar__title">
            {{ (isEdit ? 'modules.form.editTitle' : 'modules.form.createTitle') | translate }}
          </span>
        </div>
        @if (form.dirty) {
          <span class="mf-dirty-pill" data-testid="module-form-dirty">
            <span class="mf-dirty-pill__dot" aria-hidden="true"></span>
            {{ 'modules.form.dirty' | translate }}
          </span>
        }
      </div>
      <div class="mf-save-bar__right">
        @let incompleteSteps = isReadOnly() ? [] : incompleteConditionSteps();
        @if (incompleteSteps.length) {
          <p id="mf-save-blocked-condition" class="mf-save-blocked" data-testid="mf-save-blocked-condition">
            <mat-icon class="mf-save-blocked__icon" aria-hidden="true">info</mat-icon>
            <span>{{ 'modules.form.saveBlockedCondition' | translate: { steps: incompleteSteps.join(', ') } }}</span>
          </p>
        }
        @if (!isReadOnly() && stepRowOpen()) {
          <p id="mf-save-blocked-step-open" class="mf-save-blocked" data-testid="mf-save-blocked-step-open">
            <mat-icon class="mf-save-blocked__icon" aria-hidden="true">info</mat-icon>
            <span>{{ 'modules.form.saveBlockedStepOpen' | translate }}</span>
          </p>
        }
        @if (!isReadOnly()) {
          @let runBlockedKey = runBlockedHintKey();
          <span
            class="mf-save-bar__run"
            data-testid="module-run-open-huelle"
            [matTooltip]="runBlockedKey ? (runBlockedKey | translate) : ''"
            [attr.tabindex]="runBlockedKey ? 0 : null"
          >
            <button
              mat-stroked-button
              type="button"
              data-testid="module-run-open"
              [disabled]="runDisabled()"
              (click)="openRunDialog()"
            >
              <mat-icon aria-hidden="true">play_arrow</mat-icon>
              {{ 'modules.run.open' | translate }}
            </button>
          </span>
        }
        <button mat-stroked-button type="button" data-testid="module-form-cancel" (click)="onCancel()">
          {{ 'modules.form.cancel' | translate }}
        </button>
        @if (!isReadOnly()) {
          <button
            mat-flat-button
            color="primary"
            type="button"
            data-testid="module-form-save"
            [disabled]="saveDisabled()"
            [attr.aria-describedby]="saveBlockedDescribedBy(!!incompleteSteps.length)"
            (click)="onSave()"
          >
            <mat-icon aria-hidden="true">check</mat-icon>
            {{ (isEdit ? 'modules.form.save' : 'modules.form.saveNew') | translate }}
          </button>
        }
      </div>
    </div>
  </div>

  @if (isArchived()) {
    <app-projekt-archiviert-hinweis class="mf-archiviert-hinweis" />
  } @else if (isProjectReader()) {
    <div class="mf-readonly-hint">
      <mat-icon class="mf-readonly-hint__icon" aria-hidden="true">info</mat-icon>
      <p class="mf-readonly-hint__text">{{ 'modules.form.readOnlyHint' | translate }}</p>
    </div>
  }

  @if (loading()) {
    <div class="mf-page__loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (loadError()) {
    <p class="mf-page__error" role="alert">{{ 'modules.form.loadError' | translate }}</p>
  } @else {
    <div class="mf-body">
      <div class="mf-main">
        @if (saveError()) {
          <p class="mf-hint mf-hint--error" role="alert" aria-live="polite" data-testid="mf-save-error">
            @if (saveStale()) {
              {{ 'modules.form.saveStale' | translate }}
            } @else if (secretColumnError()) {
              {{ 'modules.form.saveSecretColumn' | translate }}
            } @else {
              {{ 'modules.form.saveError' | translate }}
            }
          </p>
        }

        <!-- Grunddaten -->
        <div class="mf-card" [formGroup]="form">
          <div class="mf-card__body">
            <to-section-title
              [title]="'modules.form.section.basics' | translate"
              [subtitle]="'modules.form.section.basicsSubtitle' | translate"
            />
            <div class="mf-fields">
              <mat-form-field appearance="outline" floatLabel="always" class="mf-field mf-field--uppercase-label">
                <mat-label>{{ 'modules.form.field.title' | translate }}</mat-label>
                <input
                  matInput
                  formControlName="title"
                  [attr.maxlength]="fieldLengths.NAME_MAX"
                  [placeholder]="'modules.form.field.titlePlaceholder' | translate"
                />
                <mat-hint align="end" aria-live="polite">
                  {{ form.controls.title.value.length }} / {{ fieldLengths.NAME_MAX }}
                </mat-hint>
                @if (form.controls.title.hasError('required') && form.controls.title.touched) {
                  <mat-error>{{ 'modules.form.field.titleRequired' | translate }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always" class="mf-field mf-field--uppercase-label">
                <mat-label>{{ 'modules.form.field.description' | translate }}</mat-label>
                <textarea
                  matInput
                  rows="3"
                  formControlName="description"
                  [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  [placeholder]="'modules.form.field.descriptionPlaceholder' | translate"
                ></textarea>
                <mat-hint align="end" aria-live="polite">
                  {{ form.controls.description.value.length }} / {{ fieldLengths.FREETEXT_MAX }}
                </mat-hint>
              </mat-form-field>
            </div>
          </div>
        </div>

        @if (stepsReady()) {
          <app-test-module-parameters-card
            [parameters]="draftParameters()"
            [returns]="draftReturns()"
            [readOnly]="isReadOnly()"
            (parametersChange)="onParametersChanged($event)"
            (returnsChange)="onReturnsChanged($event)"
            (invalidChange)="onParametersInvalid($event)"
          />
        }

        @if (stepsReady()) {
          <app-test-step-table
            #stepTable
            [projectId]="projectId"
            [moduleId]="moduleId"
            kontext="modul"
            [allowModuleEmbed]="true"
            [bufferedMode]="true"
            [initialSteps]="draftSteps()"
            [readOnly]="isReadOnly()"
            [availableSheets]="availableSheets()"
            [recursiveModuleIds]="recursiveModuleIds()"
            (stepsChanged)="onStepsChanged($event)"
            (moduleOpen)="onOpenModule($event)"
            (editModule)="onEditModule($event)"
          />
        }

        <app-module-edit-drawer
          [projectId]="projectId"
          [moduleId]="editingModuleId()"
          [hostModuleId]="moduleId"
          [availableSheets]="availableSheets()"
          [contextStep]="editingContextStep()"
          [contextOverride]="editingContextOverride()"
          [readOnly]="isReadOnly()"
          (closed)="onModuleDrawerClosed()"
          (tdbOverrideApplied)="onTdbOverrideApplied($event)"
          (sheetsChanged)="availableSheets.set($event)"
        ></app-module-edit-drawer>

        <div class="mf-card" data-testid="module-testdaten-card">
          <div class="mf-card__body">
            <to-section-title
              [title]="'modules.form.section.testdatenblaetter' | translate"
              [subtitle]="'modules.form.section.testdatenblaetterSubtitle' | translate"
            >
              @if (!isReadOnly()) {
                <div right>
                  <button
                    mat-flat-button
                    color="primary"
                    type="button"
                    class="mf-btn mf-btn--primary"
                    data-testid="tdb-verbinden"
                    (click)="showTdbDialog.set(true)"
                  >
                    <mat-icon aria-hidden="true">add</mat-icon>
                    {{ 'modules.form.testdatenblaetter.add' | translate }}
                  </button>
                </div>
              }
            </to-section-title>

            @if (linkedSheets().length === 0) {
              <div class="mf-empty-block">
                <p class="mf-empty">{{ 'modules.form.testdatenblaetter.empty' | translate }}</p>
                <p class="mf-empty mf-empty--hint">{{ 'modules.form.testdatenblaetter.emptyHint' | translate }}</p>
                @if (!isReadOnly()) {
                  <div class="mf-empty-actions">
                    <button
                      type="button"
                      class="mf-btn mf-btn--primary"
                      data-testid="tdb-verbinden-empty"
                      (click)="showTdbDialog.set(true)"
                    >
                      <mat-icon aria-hidden="true">add</mat-icon>
                      {{ 'modules.form.testdatenblaetter.add' | translate }}
                    </button>
                  </div>
                }
              </div>
            } @else {
              <ul class="mf-sheets" role="list">
                @for (sheet of linkedSheets(); track sheet.id) {
                  <li class="mf-sheet-item" [attr.data-testid]="'tdb-row-' + sheet.id">
                    <div class="mf-sheet">
                      <span class="mf-sheet__icon" aria-hidden="true"><mat-icon>grid_on</mat-icon></span>
                      <code class="mf-sheet__id">{{ sheet.key }}</code>
                      <div class="mf-sheet__text">
                        <span class="mf-sheet__name">{{ sheet.name }}</span>
                        <span class="mf-sheet__meta">
                          {{
                            'modules.form.testdatenblaetter.meta'
                              | translate
                                : {
                                    rows: sheet.zeilen.length,
                                    vars: sheet.spalten.length,
                                    cols: sheet.spalten.join(', '),
                                  }
                          }}
                        </span>
                      </div>
                      <span class="mf-sheet__run">
                        {{ 'modules.form.testdatenblaetter.runBadge' | translate: { count: sheet.zeilen.length } }}
                      </span>
                      @if (!isReadOnly()) {
                        <button
                          type="button"
                          class="mf-icon-btn mf-icon-btn--danger"
                          [attr.data-testid]="'tdb-trennen-' + sheet.id"
                          [attr.title]="'modules.form.testdatenblaetter.unlink' | translate"
                          [attr.aria-label]="'modules.form.testdatenblaetter.unlink' | translate"
                          (click)="onRemoveSheet(sheet)"
                        >
                          <mat-icon aria-hidden="true">close</mat-icon>
                        </button>
                      }
                    </div>
                    <app-test-module-column-mappings-panel
                      [sheet]="sheet"
                      [parameters]="inputParametersForMapping()"
                      [mappings]="draftColumnMappings()"
                      [readOnly]="isReadOnly()"
                      [secretError]="secretErrorFor(sheet.id)"
                      (mappingsChange)="onSheetMappingsChanged(sheet.id, $event)"
                    />
                  </li>
                }
              </ul>
            }
          </div>
        </div>

        <app-module-details-historie
          [projectId]="projectId"
          [moduleId]="moduleId"
          [readonly]="isReadOnly()"
          [personOptions]="personOptions()"
          [currentUserId]="currentUserId()"
          [notesFilled]="notizGesetzt()"
          (chainChanged)="onHandoverChainChanged()"
        >
          <div class="mf-notizen" [formGroup]="form">
            <label class="mf-notizen__label" for="mod-notizen-feld" data-testid="mod-notizen-label">
              {{ 'modules.form.historie.notizLabel' | translate }}
            </label>
            <textarea
              id="mod-notizen-feld"
              class="mf-notizen__feld"
              formControlName="notes"
              rows="3"
              data-testid="mod-notizen"
              [attr.maxlength]="fieldLengths.FREETEXT_MAX"
              [placeholder]="'modules.form.historie.notizPlatzhalter' | translate"
            ></textarea>
            <div class="mf-notizen__zaehler" aria-live="polite" data-testid="mod-notizen-zaehler">
              {{ form.controls.notes.value.length }} / {{ fieldLengths.FREETEXT_MAX }}
            </div>
          </div>
        </app-module-details-historie>
      </div>

      <!-- Sidebar -->
      <div class="mf-sidebar">
        <div class="mf-card" [formGroup]="form">
          <div class="mf-card__body">
            <span class="mf-card__title mf-card__title--eyebrow">
              {{ 'modules.form.section.properties' | translate }}
            </span>
            <div class="mf-fields mf-fields--properties">
              <mat-form-field
                appearance="outline"
                floatLabel="always"
                subscriptSizing="dynamic"
                class="mf-field mf-field--uppercase-label"
              >
                <mat-label>{{ 'modules.form.field.status' | translate }}</mat-label>
                <mat-select formControlName="status" required>
                  @for (status of statusOptions; track status) {
                    <mat-option [value]="status">{{ 'modules.status.' + status | translate }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>

              <mat-form-field
                appearance="outline"
                floatLabel="always"
                subscriptSizing="dynamic"
                class="mf-field mf-field--uppercase-label"
              >
                <mat-label>{{ 'modules.form.automation.statusLabel' | translate }}</mat-label>
                <mat-select formControlName="automationStatus" data-testid="module-automation-status">
                  @for (option of automationStatusOptions; track option.value) {
                    <mat-option [value]="option.value">{{ option.labelKey | translate }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>

              <div class="mf-field">
                <app-person-select
                  variant="bundle"
                  formControlName="authorUserId"
                  [label]="'modules.form.field.author' | translate"
                  [options]="personOptions()"
                  [allowNone]="false"
                  [required]="true"
                ></app-person-select>
                @if (form.controls.authorUserId.hasError('required') && form.controls.authorUserId.touched) {
                  <p class="mf-field-error" role="alert">{{ 'modules.form.field.authorRequired' | translate }}</p>
                }
              </div>

              <div class="mf-field">
                <mat-form-field
                  appearance="outline"
                  floatLabel="always"
                  subscriptSizing="dynamic"
                  class="mf-field mf-field--uppercase-label"
                >
                  <mat-label>{{ 'modules.form.field.tags' | translate }}</mat-label>
                  <input
                    matInput
                    formControlName="tags"
                    data-testid="module-tags"
                    [placeholder]="'modules.form.field.tagsPlaceholder' | translate"
                  />
                </mat-form-field>
                @if (form.controls.tags.hasError('tagTooLong')) {
                  <p class="mf-field-error" role="alert" data-testid="module-tags-error">
                    {{ 'modules.form.field.tagTooLong' | translate }}
                  </p>
                }
              </div>
            </div>
          </div>
        </div>

        <app-module-usage class="mf-sidebar__card" [projectId]="projectId" [moduleId]="moduleId" />

        <app-test-module-automation-card
          class="mf-sidebar__card"
          [frameworkControl]="form.controls.automationFramework"
          [automationKey]="automationKey()"
          [readOnly]="isReadOnly()"
        />

        @if (isEdit && moduleId) {
          <app-project-documents-card
            class="mf-sidebar__card"
            [projectId]="projectId"
            targetType="test-modules"
            [targetId]="moduleId"
            [canWrite]="!isReadOnly()"
          />
        }
      </div>
    </div>
  }
</div>

<app-testdatenblatt-embed-dialog
  [projectId]="projectId"
  [excludeIds]="tdbExcludeIds()"
  [(visible)]="showTdbDialog"
  (applied)="onTdbApplied($event)"
  (createNew)="onCreateNewSheet()"
/>

<lib-modal
  [closeLabel]="'general.close' | translate"
  [title]="'common.unsavedChanges.title' | translate"
  [text]="'modules.form.unsavedMessage' | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'common.unsavedChanges.leave' | translate"
  [cancelText]="'common.unsavedChanges.stay' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
